import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export default function Siparisler() {
  const { profil } = useAuth();
  const navigate = useNavigate();

  // Geri dönünce filtre/sayfa/scroll korunsun (telefonda "başa dönme" sorununu çözer)
  const DURUM_KEY = 'pisipro_siparisler_durum';
  const kayitliDurum = (() => {
    try { return JSON.parse(sessionStorage.getItem(DURUM_KEY)) || {}; } catch { return {}; }
  })();
  const scrollGeriYapildi = useRef(false);

  function scrollKabi() {
    // Layout'taki kaydırılabilir içerik alanı (yoksa window)
    const main = document.querySelector('main');
    return (main && main.lastElementChild) || null;
  }
  function durumKaydet(ekstra) {
    try {
      const mevcut = JSON.parse(sessionStorage.getItem(DURUM_KEY) || '{}');
      sessionStorage.setItem(DURUM_KEY, JSON.stringify({ ...mevcut, filtre, sayfa, ...(ekstra || {}) }));
    } catch {}
  }
  function detayaGit(id) {
    const el = scrollKabi();
    durumKaydet({ scrollTop: el ? el.scrollTop : (window.scrollY || 0) });
    navigate(`/siparisler/${id}`);
  }

  const [siparisler, setSiparisler] = useState([]);
  const [kalemMap, setKalemMap] = useState({});
  const [kargoInputs, setKargoInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [sayfa, setSayfa] = useState(kayitliDurum.sayfa || 1);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [adresPopup, setAdresPopup] = useState(null);
  const [urunPopup, setUrunPopup] = useState(null);
  const [seciliSiparisler, setSeciliSiparisler] = useState(new Set());
  const SAYFA_BOYUTU = profil?.rol === 'temsilci' ? 10 : 100;

  const [filtre, setFiltre] = useState(kayitliDurum.filtre || {
    siparis_no: '', musteri_ara: '', siparis_durumu: '', odeme_durumu: '', komisyon_durumu: '',
    firma_id: '', tarih_bas: '', tarih_bit: '', temsilci_id: '',
  });
  const [firmalar, setFirmalar] = useState([]);
  const [temsilciler, setTemsilciler] = useState([]);
  const [toplamlar, setToplamlar] = useState({ tumTutar: 0, odenenTutar: 0, odenmemisTutar: 0, toplamAdet: 0 });

  useEffect(() => {
    async function fetchFirmalar() {
      let q = supabase.from('firmalar').select('id, ad').eq('aktif', true);
      if (profil?.rol === 'temsilci') {
        const { data: yetkiler } = await supabase.from('temsilci_firma_yetki')
          .select('firma_id').eq('kullanici_id', profil.id);
        const ids = (yetkiler || []).map(y => y.firma_id);
        q = ids.length > 0 ? q.in('id', ids) : q.eq('id', '00000000-0000-0000-0000-000000000000');
      }
      const { data } = await q;
      const FIRMA_SIRA = {
        'acb52946-e2a8-4fac-84db-04865b85f944': 0,
        '5556578b-e620-4abc-837f-fb8549cfe512': 1,
        '408634a3-a463-43e8-b01e-2e55ce7ad9fb': 2,
      };
      setFirmalar((data || []).sort((a, b) => (FIRMA_SIRA[a.id] ?? 99) - (FIRMA_SIRA[b.id] ?? 99)));
    }
    if (profil?.id) fetchFirmalar();
    if (profil?.rol !== 'temsilci') {
      supabase.from('kullanicilar').select('id, ad, soyad').in('rol', ['temsilci', 'admin', 'super_admin']).eq('aktif', true).then(({ data }) => setTemsilciler(data || []));
    }
  }, [profil]);

  // filtre/sayfa degisince hafizaya yaz (geri donunce ayni filtre gelsin)
  useEffect(() => {
    durumKaydet();
  }, [filtre, sayfa]);

  // Geri donuldugunde (liste ilk kez yuklenince) eski scroll konumuna don — bir kez
  useEffect(() => {
    if (loading || scrollGeriYapildi.current) return;
    scrollGeriYapildi.current = true;
    const st = kayitliDurum.scrollTop;
    if (!st) return;
    const el = scrollKabi();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (el) el.scrollTop = st; else window.scrollTo(0, st);
    }));
  }, [loading]);

  useEffect(() => {
    // Profil yüklenmeden sipariş çekme — yoksa filtre uygulanmadan tüm siparişler görünür.
    // NOT: Bagimlilik 'profil' NESNESI degil, sabit alanlar (id + rol). Auth token yenilenince
    // profil yeni bir NESNE olur; nesneye baglanirsa effect bosu bosuna tekrar calisir, sayfa
    // surekli render olur ve tarih takvimi acilir acilmaz kapanir.
    if (!profil?.id) return;
    fetchSiparisler();
  }, [filtre, sayfa, profil?.id, profil?.rol]);

  async function fetchSiparisler() {
    if (!profil?.id) return;   // profil gelmeden asla veri çekme
    setLoading(true);

    // Musteri adi/telefon aramasi: once eslesen musteri id'lerini bul (tum kayitlarda arama icin)
    let araMusteriIds = null;
    const araStr = (filtre.musteri_ara || '').trim();
    if (araStr) {
      const { data: mEsles } = await supabase.from('musteriler')
        .select('id').or(`ad.ilike.%${araStr}%,telefon.ilike.%${araStr}%`);
      araMusteriIds = (mEsles || []).map(m => m.id);
    }

    function filtreUygula(q) {
      if (profil?.rol === 'temsilci') q = q.eq('temsilci_id', profil.id);
      if (filtre.siparis_durumu) q = q.eq('siparis_durumu', filtre.siparis_durumu);
      if (filtre.odeme_durumu) q = q.eq('odeme_durumu', filtre.odeme_durumu);
      if (filtre.komisyon_durumu === 'odendi') q = q.eq('komisyon_odendi', true);
      if (filtre.komisyon_durumu === 'odenmedi') q = q.eq('komisyon_odendi', false);
      if (filtre.firma_id) q = q.eq('firma_id', filtre.firma_id);
      if (filtre.temsilci_id) q = q.eq('temsilci_id', filtre.temsilci_id);
      if (filtre.tarih_bas) q = q.gte('created_at', filtre.tarih_bas);
      if (filtre.tarih_bit) q = q.lte('created_at', filtre.tarih_bit + 'T23:59:59');
      // Arama: siparis_no VEYA eslesen musterilerden biri
      if (araStr) {
        const parcalar = [`siparis_no.ilike.%${araStr}%`];
        if (araMusteriIds && araMusteriIds.length > 0) {
          parcalar.push(`musteri_id.in.(${araMusteriIds.join(',')})`);
        }
        q = q.or(parcalar.join(','));
      }
      return q;
    }

    let q = supabase.from('siparisler')
      .select('id, siparis_no, siparis_durumu, odeme_durumu, komisyon_odendi, siparis_tipi, kargo_kodu, toplam_tutar, created_at, adres, musteri_id, firma_id, temsilci_id, depo_id')
      .order('created_at', { ascending: false });
    q = filtreUygula(q);
    const from = (sayfa - 1) * SAYFA_BOYUTU;
    q = q.range(from, from + SAYFA_BOYUTU - 1);

    // Önce LİSTEYİ çek — hemen görünsün
    const { data } = await q;
    const liste = data || [];

    // Toplam kartlari + sayfa sayisi: TUM kayitlari (1000 limitini asarak) ARKA PLANDA hesapla.
    // Boylece liste aninda gelir; uzun hesap UI'yi ve tarih takvimini bloklamaz.
    (async () => {
      const TOPLAM_SAYFA = 1000;
      let tumListe = [];
      for (let i = 0; i < 1000; i++) {
        let tq = supabase.from('siparisler').select('toplam_tutar, odeme_durumu');
        tq = filtreUygula(tq);
        tq = tq.range(i * TOPLAM_SAYFA, (i + 1) * TOPLAM_SAYFA - 1);
        const { data: chunk, error: tErr } = await tq;
        if (tErr) { console.error('toplam hesap hatasi', tErr); break; }
        tumListe = tumListe.concat(chunk || []);
        if (!chunk || chunk.length < TOPLAM_SAYFA) break;
      }
      setToplamSayfa(Math.max(1, Math.ceil(tumListe.length / SAYFA_BOYUTU)));
      const tumTutar = tumListe.reduce((a, s) => a + (s.toplam_tutar || 0), 0);
      const odenenTutar = tumListe.filter(s => s.odeme_durumu === 'odendi').reduce((a, s) => a + (s.toplam_tutar || 0), 0);
      const odenmemisTutar = tumListe.filter(s => s.odeme_durumu !== 'odendi').reduce((a, s) => a + (s.toplam_tutar || 0), 0);
      setToplamlar({ tumTutar, odenenTutar, odenmemisTutar, toplamAdet: tumListe.length });
    })();

    if (liste.length > 0) {
      const ids = liste.map(s => s.id);
      const musteriIds = [...new Set(liste.map(s => s.musteri_id).filter(Boolean))];
      const firmaIds = [...new Set(liste.map(s => s.firma_id).filter(Boolean))];

      const [{ data: musteriler }, { data: firmaData }, { data: temsilciData }, { data: kalemler }] = await Promise.all([
        musteriIds.length ? supabase.from('musteriler').select('id, ad, telefon, sehir, adres').in('id', musteriIds) : { data: [] },
        firmaIds.length ? supabase.from('firmalar').select('id, ad').in('id', firmaIds) : { data: [] },
        supabase.from('kullanicilar').select('id, ad, soyad').in('rol', ['temsilci', 'admin', 'super_admin']).eq('aktif', true),
        supabase.from('siparis_kalemleri').select('siparis_id, miktar, urunler(ad)').in('siparis_id', ids),
      ]);

      const musteriMap = {};
      (musteriler || []).forEach(m => { musteriMap[m.id] = m; });
      const firmaMap = {};
      (firmaData || []).forEach(f => { firmaMap[f.id] = f; });
      const temsilciMap = {};
      (temsilciData || []).forEach(t => { temsilciMap[t.id] = `${t.ad} ${t.soyad}`; });

      let sonuclar = liste.map(s => ({
        ...s,
        musteriler: musteriMap[s.musteri_id] || null,
        firmalar: firmaMap[s.firma_id] || null,
        temsilci_ad: temsilciMap[s.temsilci_id] || '-',
      }));

      setSiparisler(sonuclar);

      const map = {};
      (kalemler || []).forEach(k => {
        if (!map[k.siparis_id]) map[k.siparis_id] = [];
        map[k.siparis_id].push({ ad: k.urunler?.ad, miktar: k.miktar });
      });
      setKalemMap(map);
    } else {
      setSiparisler([]);
    }
    setLoading(false);
  }

  async function kargoKaydet(e, siparisId) {
    e.stopPropagation();
    const kod = kargoInputs[siparisId]?.trim();
    if (!kod) return;
    await supabase.from('siparisler').update({ kargo_kodu: kod, siparis_durumu: 'kargoda' }).eq('id', siparisId);
    fetchSiparisler();
  }

  async function handleSil(e, siparisId) {
    e.stopPropagation();
    if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;
    try {
      const { data: siparis } = await supabase.from('siparisler').select('depo_id').eq('id', siparisId).single();
      const { data: kalemler } = await supabase.from('siparis_kalemleri').select('urun_id, miktar').eq('siparis_id', siparisId);
      if (siparis?.depo_id && kalemler?.length > 0) {
        for (const kalem of kalemler) {
          const { data: stok } = await supabase.from('depo_stok').select('id, miktar').eq('depo_id', siparis.depo_id).eq('urun_id', kalem.urun_id).maybeSingle();
          const onceki = Number(stok?.miktar) || 0;
          const yeniMiktar = onceki + Number(kalem.miktar);
          if (stok?.id) {
            await supabase.from('depo_stok').update({ miktar: yeniMiktar }).eq('id', stok.id);
          } else {
            await supabase.from('depo_stok').insert({ depo_id: siparis.depo_id, urun_id: kalem.urun_id, miktar: yeniMiktar });
          }
        }
      }
      await supabase.from('ust_temsilci_komisyonlar').delete().eq('siparis_id', siparisId);
      await supabase.from('kargo_loglari').delete().eq('siparis_id', siparisId);
      await supabase.from('odeme_dekontlari').delete().eq('siparis_id', siparisId);
      await supabase.from('stok_hareketleri').delete().eq('siparis_id', siparisId);
      await supabase.from('siparis_kalemleri').delete().eq('siparis_id', siparisId);
      const { error } = await supabase.from('siparisler').delete().eq('id', siparisId);
      if (error) { alert('Silinemedi: ' + error.message); return; }
      fetchSiparisler();
    } catch (err) {
      alert('Hata: ' + err.message);
    }
  }

  function topluWhatsapp() {
    const seciliListe = siparisler.filter(s => seciliSiparisler.has(s.id));
    const musteriGrup = {};
    seciliListe.forEach(s => {
      const mid = s.musteri_id;
      if (!musteriGrup[mid]) musteriGrup[mid] = { musteri: s.musteriler, siparisler: [] };
      musteriGrup[mid].siparisler.push(s);
    });
    Object.values(musteriGrup).forEach((grup, i) => {
      setTimeout(() => {
        const { musteri, siparisler: sList } = grup;
        const tel = (musteri?.telefon || '').replace(/\D/g, '').replace(/^0/, '');
        const toplam = sList.reduce((a, s) => a + (s.toplam_tutar || 0), 0);
        const satirlar = sList.map(s => {
          const urunler = (kalemMap[s.id] || []).map(k => `   ${k.ad} x${k.miktar}`).join('\n');
          const tarih = new Date(new Date(s.created_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('tr-TR');
          return `📦 Sipariş #${s.siparis_no} - ${tarih}\n${urunler}\n   → ${(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺`;
        }).join('\n\n');
        const mesaj = `Merhaba ${musteri?.ad || ''},\n\n${sList.length === 1 ? 'Aşağıdaki siparişinize ait ödemeyi muhasebe kayıtlarımızda bulamadık.' : 'Aşağıdaki siparişlerinize ait ödemeyi muhasebe kayıtlarımızda bulamadık.'}\n\n${satirlar}\n\n💰 Toplam Tutar: ${toplam.toLocaleString('tr-TR')} ₺\n\nEğer ödemenizi gerçekleştirdiyseniz, dekontunuzu bu mesaja yanıt olarak iletmenizi rica ederiz.\nHenüz ödeme yapılmadıysa, hatırlatmak istedik.\n\nİlginiz için teşekkür eder, iyi günler dileriz. 🙏`;
        const waUrl = tel ? `https://wa.me/90${tel}?text=${encodeURIComponent(mesaj)}` : `https://wa.me/?text=${encodeURIComponent(mesaj)}`;
        window.open(waUrl, '_blank');
      }, i * 1000);
    });
  }

  function filtreGuncelle(key, val) {
    setFiltre(f => ({ ...f, [key]: val }));
    setSayfa(1);
  }

  function filtreTemizle() {
    setFiltre({ siparis_no: '', musteri_ara: '', siparis_durumu: '', odeme_durumu: '', komisyon_durumu: '', firma_id: '', tarih_bas: '', tarih_bit: '', temsilci_id: '' });
    setSayfa(1);
  }

  function teslimatBilgisi(s) {
    if (s.siparis_durumu === 'teslim') return <span className="badge badge-green">✓ {s.kargo_kodu || 'Teslim Edildi'}</span>;
    if (s.siparis_durumu === 'kargoda' && s.kargo_kodu && !kargoInputs[s.id + '_edit']) return (
      <div onClick={e => e.stopPropagation()}>
        <span className="badge badge-orange" style={{ display: 'block', marginBottom: 4 }}>🚚 Kargoda</span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2 }}>
          <span style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'monospace' }}>{s.kargo_kodu}</span>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '1px 5px' }}
            onClick={e => { e.stopPropagation(); setKargoInputs(m => ({ ...m, [s.id + '_edit']: true, [s.id]: s.kargo_kodu })); }}>✏️</button>
        </div>
      </div>
    );
    if (s.siparis_durumu === 'kargoda' && s.kargo_kodu && kargoInputs[s.id + '_edit']) return (
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input className="input" style={{ padding: '4px 8px', fontSize: 12, width: 110, height: 28 }}
          value={kargoInputs[s.id] || ''}
          onChange={e => setKargoInputs(m => ({ ...m, [s.id]: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') { kargoKaydet(e, s.id); setKargoInputs(m => ({ ...m, [s.id + '_edit']: false })); } }}
          autoFocus />
        <button className="btn btn-primary btn-sm" style={{ padding: '4px 8px', fontSize: 11, height: 28 }}
          onClick={e => { kargoKaydet(e, s.id); setKargoInputs(m => ({ ...m, [s.id + '_edit']: false })); }}>✓</button>
        <button className="btn btn-ghost btn-sm" style={{ padding: '4px 8px', fontSize: 11, height: 28 }}
          onClick={e => { e.stopPropagation(); setKargoInputs(m => ({ ...m, [s.id + '_edit']: false })); }}>✕</button>
      </div>
    );
    if (s.siparis_durumu === 'iade') return <span className="badge badge-red">↩ İade</span>;
    if (s.siparis_durumu === 'iptal') return <span className="badge badge-gray">✗ İptal</span>;
    return (
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input className="input" style={{ padding: '4px 8px', fontSize: 12, width: 110, height: 28 }}
          placeholder="Takip no..."
          value={kargoInputs[s.id] || ''}
          onChange={e => setKargoInputs(m => ({ ...m, [s.id]: e.target.value }))}
          onKeyDown={e => e.key === 'Enter' && kargoKaydet(e, s.id)} />
        <button className="btn btn-primary btn-sm" style={{ padding: '4px 8px', fontSize: 11, height: 28 }}
          onClick={e => kargoKaydet(e, s.id)} disabled={!kargoInputs[s.id]?.trim()}>✓</button>
      </div>
    );
  }

  async function excelExport() {
    const XLSX = await import('xlsx');
    const rows = siparisler.map(s => ({
      'Sipariş No': s.siparis_no, 'Müşteri': s.musteriler?.ad,
      'Telefon': s.musteriler?.telefon, 'Adres': s.adres || s.musteriler?.adres,
      'Ürünler': (kalemMap[s.id] || []).map(k => `${k.ad} x${k.miktar}`).join(', '),
      'Kargo No': s.kargo_kodu, 'Durum': s.siparis_durumu, 'Ödeme': s.odeme_durumu,
      'Tarih': new Date(new Date(s.created_at).getTime() + 3 * 60 * 60 * 1000).toLocaleString('tr-TR'),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Siparişler');
    XLSX.writeFile(wb, `siparisler_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`);
  }

  const adresPopupSiparis = siparisler.find(x => x.id === adresPopup);
  const urunPopupSiparis = siparisler.find(x => x.id === urunPopup);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Siparişler</h1>
          <p className="page-subtitle">{toplamlar.toplamAdet} sipariş</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={excelExport}>📊 Excel</button>
          {seciliSiparisler.size > 0 && (
            <button className="btn btn-primary" style={{ background: '#25D366', borderColor: '#25D366' }} onClick={topluWhatsapp}>
              📱 Ödeme Takibi Gönder ({seciliSiparisler.size})
            </button>
          )}
          <button className="btn btn-primary hide-mobile" onClick={() => navigate('/siparisler/yeni')}>+ Yeni Sipariş</button>
        </div>
      </div>

      {profil?.rol !== 'depocu' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          <div className="stat-card">
            <div className="stat-label">Toplam Satış</div>
            <div className="stat-value" style={{ fontSize: 18, color: 'var(--text)' }}>{toplamlar.tumTutar.toLocaleString('tr-TR')} ₺</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">✅ Ödenen</div>
            <div className="stat-value" style={{ fontSize: 18, color: 'var(--green)' }}>{toplamlar.odenenTutar.toLocaleString('tr-TR')} ₺</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">❌ Ödenmemiş</div>
            <div className="stat-value" style={{ fontSize: 18, color: 'var(--red)' }}>{toplamlar.odenmemisTutar.toLocaleString('tr-TR')} ₺</div>
          </div>
        </div>
      )}

      <div className="filter-bar filter-bar-grid">
          <input className="input" placeholder="Müşteri adı, telefon veya sipariş no..." value={filtre.musteri_ara} onChange={e => filtreGuncelle('musteri_ara', e.target.value)} style={{ gridColumn: '1 / -1' }} />
          <select className="select" value={filtre.siparis_durumu} onChange={e => filtreGuncelle('siparis_durumu', e.target.value)}>
            <option value="">Tüm Durumlar</option>
            <option value="alinidi">Alındı</option>
            <option value="hazirlaniyor">Hazırlanıyor</option>
            <option value="kargoda">Kargoda</option>
            <option value="teslim">Teslim</option>
            <option value="iade">İade</option>
            <option value="iptal">İptal</option>
          </select>
          <select className="select" value={filtre.odeme_durumu} onChange={e => filtreGuncelle('odeme_durumu', e.target.value)}>
            <option value="">Tüm Ödemeler</option>
            <option value="odendi">Ödendi</option>
            <option value="odenmedi">Ödenmedi</option>
          </select>
          <select className="select" value={filtre.komisyon_durumu} onChange={e => filtreGuncelle('komisyon_durumu', e.target.value)}>
            <option value="">Tüm Komisyonlar</option>
            <option value="odendi">Komisyon Ödendi</option>
            <option value="odenmedi">Komisyon Ödenmedi</option>
          </select>
          <select className="select" value={filtre.firma_id} onChange={e => filtreGuncelle('firma_id', e.target.value)}>
            <option value="">Tüm Firmalar</option>
            {firmalar.map(f => <option key={f.id} value={f.id}>{f.ad}</option>)}
          </select>
          {profil?.rol !== 'temsilci' && (
            <select className="select" value={filtre.temsilci_id} onChange={e => filtreGuncelle('temsilci_id', e.target.value)}>
              <option value="">Tüm Temsilciler</option>
              {temsilciler.map(t => <option key={t.id} value={t.id}>{t.ad} {t.soyad}</option>)}
            </select>
          )}
          <label style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Başlangıç
            <input type="date" className="input" value={filtre.tarih_bas} onChange={e => filtreGuncelle('tarih_bas', e.target.value)} />
          </label>
          <label style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            Bitiş
            <input type="date" className="input" value={filtre.tarih_bit} onChange={e => filtreGuncelle('tarih_bit', e.target.value)} />
          </label>
          <button className="btn btn-ghost btn-sm" onClick={filtreTemizle} style={{ alignSelf: 'end', height: 38 }}>✕ Temizle</button>
        </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Sipariş No</th>
                <th>Müşteri</th>
                <th>Adres</th>
                <th>Ürünler</th>
                <th>Kargo / Teslimat</th>
                {profil?.rol !== 'temsilci' && profil?.rol !== 'depocu' && <th>Temsilci</th>}
                {profil?.rol !== 'depocu' && <th>Ödeme</th>}
                <th>Tarih</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="loading">⏳ Yükleniyor...</td></tr>
              ) : siparisler.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>Sipariş bulunamadı</td></tr>
              ) : siparisler.map(s => {
                const adres = s.adres || s.musteriler?.adres;
                const kalemler = kalemMap[s.id] || [];
                return (
                  <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => detayaGit(s.id)}>
                    <td>
                      <span className="mono" style={{ color: s.siparis_tipi === 'numune' ? '#8b5cf6' : 'var(--accent)', fontWeight: 600 }}>{s.siparis_no}</span>
                      {s.siparis_tipi === 'numune' && (
                        <div><span className="badge" style={{ background: 'rgba(139,92,246,0.15)', color: '#8b5cf6', fontSize: 10 }}>Numune</span></div>
                      )}
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {s.odeme_durumu !== 'odendi' && s.siparis_tipi !== 'numune' && (
                        <input type="checkbox" style={{ marginRight: 6 }}
                          checked={seciliSiparisler.has(s.id)}
                          onChange={e => {
                            const yeni = new Set(seciliSiparisler);
                            e.target.checked ? yeni.add(s.id) : yeni.delete(s.id);
                            setSeciliSiparisler(yeni);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                      {s.musteriler?.ad || '-'}
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 160 }} onClick={e => e.stopPropagation()}>
                      {adres ? (
                        <span onClick={() => setAdresPopup(s.id)} style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline dotted' }}>
                          📍 {adres.length > 25 ? adres.slice(0, 25) + '…' : adres}
                        </span>
                      ) : <span style={{ color: 'var(--text2)' }}>{s.musteriler?.sehir || '-'}</span>}
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 200 }} onClick={e => e.stopPropagation()}>
                      {kalemler.length === 1 ? (
                        <div>
                          <span style={{ fontWeight: 500 }}>{kalemler[0].ad}</span>
                          <span style={{ color: 'var(--accent)', marginLeft: 4 }}>x{kalemler[0].miktar}</span>
                        </div>
                      ) : kalemler.length > 1 ? (
                        <span onClick={() => setUrunPopup(s.id)} style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline dotted' }}>
                          📦 {kalemler.length} ürün 👆
                        </span>
                      ) : <span style={{ color: 'var(--text2)' }}>—</span>}
                    </td>
                    <td>{teslimatBilgisi(s)}</td>
                    {profil?.rol !== 'temsilci' && profil?.rol !== 'depocu' && (
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                        {s.temsilci_ad && s.temsilci_ad !== '-' ? s.temsilci_ad : '-'}
                      </td>
                    )}
                    {profil?.rol !== 'depocu' && (
                      <td>
                        {s.siparis_tipi === 'numune' ? (
                          <span className="badge" style={{ background: 'rgba(139,92,246,0.15)', color: '#8b5cf6' }}>Bedelsiz</span>
                        ) : (
                          <span className={`badge ${s.odeme_durumu === 'odendi' ? 'badge-green' : 'badge-red'}`}>
                            {s.odeme_durumu === 'odendi' ? 'Ödendi' : 'Ödenmedi'}
                          </span>
                        )}
                        {s.siparis_tipi !== 'numune' && s.komisyon_odendi && (
                          <span className="badge badge-green" style={{ display: 'block', marginTop: 4, whiteSpace: 'nowrap' }}>✓ Kom.</span>
                        )}
                      </td>
                    )}
                    <td style={{ fontSize: 12 }}>
                      <div>{new Date(new Date(s.created_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('tr-TR')}</div>
                      <div style={{ color: 'var(--text2)' }}>{new Date(new Date(s.created_at).getTime() + 3 * 60 * 60 * 1000).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <a href={`/siparisler/${s.id}`} className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); const el = scrollKabi(); durumKaydet({ scrollTop: el ? el.scrollTop : (window.scrollY || 0) }); }}>Detay</a>
                        {(profil?.rol === 'admin' || profil?.rol === 'super_admin') && (
                          <button className="btn btn-danger btn-sm" onClick={e => handleSil(e, s.id)}>Sil</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {toplamSayfa > 1 && (
          <div className="pagination" style={{ padding: '12px 16px', justifyContent: 'flex-end' }}>
            <button onClick={() => setSayfa(p => Math.max(1, p - 1))} disabled={sayfa === 1}>‹</button>
            {Array.from({ length: Math.min(7, toplamSayfa) }, (_, i) => {
              const p = sayfa <= 4 ? i + 1 : sayfa + i - 3;
              if (p < 1 || p > toplamSayfa) return null;
              return <button key={p} className={sayfa === p ? 'active' : ''} onClick={() => setSayfa(p)}>{p}</button>;
            })}
            <button onClick={() => setSayfa(p => Math.min(toplamSayfa, p + 1))} disabled={sayfa === toplamSayfa}>›</button>
          </div>
        )}
      </div>

      {adresPopup && adresPopupSiparis && (
        <div onClick={() => setAdresPopup(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, minWidth: 300, maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10, fontWeight: 600 }}>📍 Kargo Adresi — #{adresPopupSiparis.siparis_no}</div>
            <div style={{ border: '2px solid #333', borderRadius: 8, padding: 16, marginBottom: 16, background: '#fff' }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#000', marginBottom: 10 }}>{adresPopupSiparis.musteriler?.sehir || '-'}</div>
              <div style={{ fontSize: 13, color: '#555', fontWeight: 700, marginBottom: 2 }}>TELEFON</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#000', marginBottom: 10 }}>{adresPopupSiparis.musteriler?.telefon || '-'}</div>
              <div style={{ fontSize: 13, color: '#555', fontWeight: 700, marginBottom: 2 }}>AD SOYAD</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#000', marginBottom: 10 }}>{adresPopupSiparis.musteriler?.ad || '-'}</div>
              <div style={{ fontSize: 13, color: '#555', fontWeight: 700, marginBottom: 2 }}>AÇIK ADRES</div>
              <div style={{ fontSize: 14, color: '#000', lineHeight: 1.5 }}>{adresPopupSiparis.adres || adresPopupSiparis.musteriler?.adres || '-'}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setAdresPopup(null)} style={{ background: '#f0f0f0', border: 'none', borderRadius: 8, color: '#444', cursor: 'pointer', fontSize: 13, padding: '8px 16px' }}>Kapat</button>
              <button style={{ background: '#25D366', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, padding: '8px 16px', fontWeight: 700 }}
                onClick={e => {
                  e.stopPropagation();
                  const s = adresPopupSiparis;
                  const adres = s.adres || s.musteriler?.adres || '-';
                  const metin = `${s.musteriler?.ad || ''}\n${s.musteriler?.telefon || ''}\n${s.musteriler?.sehir || ''}\n${adres}`;
                  navigator.clipboard.writeText(metin).then(() => alert('✅ Adres kopyalandı!')).catch(() => alert('Kopyalanamadı'));
                }}>📋 Adresi Kopyala</button>
              <button style={{ background: '#E07B00', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, padding: '8px 16px', fontWeight: 700 }}
                onClick={async e => {
                  e.stopPropagation();
                  const s = adresPopupSiparis;
                  const adres = s.adres || s.musteriler?.adres || '-';
                  try {
                    const canvas = document.createElement('canvas');
                    canvas.width = 600; canvas.height = 400;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 600, 400);
                    ctx.strokeStyle = '#000000'; ctx.lineWidth = 4; ctx.strokeRect(10, 10, 580, 380);
                    ctx.fillStyle = '#000000'; ctx.font = 'bold 42px Arial';
                    ctx.fillText(s.musteriler?.sehir || '-', 30, 70);
                    ctx.font = '13px Arial'; ctx.fillStyle = '#888888'; ctx.fillText('TELEFON', 30, 110);
                    ctx.font = 'bold 22px Arial'; ctx.fillStyle = '#000000'; ctx.fillText(s.musteriler?.telefon || '-', 30, 138);
                    ctx.font = '13px Arial'; ctx.fillStyle = '#888888'; ctx.fillText('AD SOYAD', 30, 175);
                    ctx.font = 'bold 22px Arial'; ctx.fillStyle = '#000000'; ctx.fillText(s.musteriler?.ad || '-', 30, 203);
                    ctx.font = '13px Arial'; ctx.fillStyle = '#888888'; ctx.fillText('AÇIK ADRES', 30, 240);
                    ctx.font = '16px Arial'; ctx.fillStyle = '#000000';
                    const words = adres.split(' '); let line = ''; let y = 265;
                    for (const word of words) {
                      const test = line ? line + ' ' + word : word;
                      if (ctx.measureText(test).width > 540 && line) { ctx.fillText(line, 30, y); line = word; y += 24; if (y > 380) break; } else { line = test; }
                    }
                    if (line && y <= 380) ctx.fillText(line, 30, y);
                    const imgData = canvas.toDataURL('image/png');
                    if (navigator.share && navigator.canShare) {
                      const blob = await (await fetch(imgData)).blob();
                      const file = new File([blob], `kargo_${s.siparis_no}.png`, { type: 'image/png' });
                      if (navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: `Kargo Etiketi #${s.siparis_no}` }); return; }
                    }
                    const a = document.createElement('a'); a.href = imgData; a.download = `kargo_${s.siparis_no}.png`; a.click();
                  } catch(err) { alert('Resim oluşturulamadı: ' + err.message); }
                }}>📸 Resim Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {urunPopup && urunPopupSiparis && (
        <div onClick={() => setUrunPopup(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, minWidth: 280, maxWidth: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10, fontWeight: 600 }}>📦 Sipariş Kalemleri</div>
            {(kalemMap[urunPopupSiparis.id] || []).map((k, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < kalemMap[urunPopupSiparis.id].length - 1 ? '1px solid #eee' : 'none' }}>
                <span style={{ color: '#222', fontSize: 14 }}>{k.ad}</span>
                <span style={{ color: '#E07B00', fontWeight: 700, marginLeft: 8 }}>x{k.miktar}</span>
              </div>
            ))}
            <button onClick={() => setUrunPopup(null)} style={{ marginTop: 16, background: '#f0f0f0', border: 'none', borderRadius: 8, color: '#444', cursor: 'pointer', fontSize: 13, padding: '8px 20px' }}>Kapat</button>
          </div>
        </div>
      )}
    </div>
  );
}
